import * as THREE from 'three';
import type { Game, GameContext, GameMeta, InputState } from '../types';
import {
  createThreeKit,
  disposeObject,
  makeGlowDisc,
  makeStars,
  resizeThree,
  type ThreeKit,
} from '../shared/threeKit';
import { OrbitRig } from '../shared/orbitRig';

type Obstacle = { mesh: THREE.Group; z: number; lane: number; tall: boolean };
type Pickup = { mesh: THREE.Group; z: number; lane: number; kind: 'note' | 'shield' | 'slow' };

/**
 * BEAT CHASE — 3-lane runner with music punch + powerups + orbit cam.
 */
export class BeatChaseGame implements Game {
  readonly meta: GameMeta = {
    id: 'trex',
    title: 'BEAT CHASE',
    blurb: '3 lanes on the beat. Collect notes, dodge traffic. Drag cam.',
    tag: 'RUNNER',
    status: 'playable',
  };

  private ctx!: GameContext;
  private kit!: ThreeKit;
  private stars!: THREE.Points;
  private runner!: THREE.Group;
  private body!: THREE.Mesh;
  private track!: THREE.Group;
  private beatRings: THREE.Mesh[] = [];
  private obstacles: Obstacle[] = [];
  private pickups: Pickup[] = [];
  private lane = 1;
  private laneX = 0;
  private hopY = 0;
  private hopV = 0;
  private hopping = false;
  private spawnCd = 0.55;
  private pickCd = 1.2;
  private invuln = 0;
  private shieldT = 0;
  private slowT = 0;
  private score = 0;
  private lives = 3;
  private wave = 1;
  private message = '←→ LANES · FIRE HOP · DRAG CAM';
  private messageT = 2.4;
  private dist = 0;
  private disposed = false;
  private laneCd = 0;
  private prevLeft = false;
  private prevRight = false;
  private orbit = new OrbitRig({ azimuth: 0, elevation: 0.55, distance: 14 });
  private camTarget = new THREE.Vector3(0, 0.5, -2);
  private readonly laneXs = [-2.4, 0, 2.4];
  private readonly groundY = 0;
  private readonly hopV0 = 8.5;
  private readonly gravity = 42;

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    this.kit = createThreeKit(ctx, {
      label: 'Beat Chase',
      z: 16,
      fov: 48,
      clear: 0x061018,
      fog: 0x061018,
      fogNear: 16,
      fogFar: 42,
    });
    this.kit.scene.add(new THREE.AmbientLight(0xb0e0ff, 0.65));
    this.stars = makeStars(this.kit.lowPower ? 60 : 140, 0x88ffcc);
    this.kit.scene.add(this.stars);

    this.track = new THREE.Group();
    for (let i = 0; i < 3; i++) {
      const lane = new THREE.Mesh(
        new THREE.BoxGeometry(2.15, 0.28, 90),
        new THREE.MeshStandardMaterial({
          color: i === 1 ? 0x10382a : 0x0c281c,
          emissive: 0x082018,
          emissiveIntensity: 0.45,
          roughness: 0.75,
        }),
      );
      lane.position.set(this.laneXs[i], this.groundY - 0.14, -22);
      this.track.add(lane);
      // lane light strip
      const strip = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 0.06, 90),
        new THREE.MeshBasicMaterial({ color: 0x33ffaa }),
      );
      strip.position.set(this.laneXs[i], this.groundY + 0.02, -22);
      this.track.add(strip);
    }
    this.kit.root.add(this.track);

    // beat pulse rings on track
    for (let i = 0; i < 6; i++) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.3, 0.55, 20),
        new THREE.MeshBasicMaterial({
          color: 0x66ffcc,
          transparent: true,
          opacity: 0.0,
          side: THREE.DoubleSide,
        }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(0, 0.05, -6 - i * 5);
      this.kit.root.add(ring);
      this.beatRings.push(ring);
    }

    this.runner = new THREE.Group();
    this.body = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.32, 0.55, 4, 10),
      new THREE.MeshStandardMaterial({
        color: 0xff4d6d,
        emissive: 0x881133,
        emissiveIntensity: 0.55,
        roughness: 0.35,
      }),
    );
    this.body.position.y = 0.7;
    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.26, 12, 12),
      new THREE.MeshStandardMaterial({ color: 0xffe4dc, emissive: 0x441122, roughness: 0.4 }),
    );
    head.position.y = 1.25;
    const disc = makeGlowDisc(0xff4d6d, 0.95, 0.45);
    disc.rotation.x = -Math.PI / 2;
    disc.position.y = 0.05;
    this.runner.add(this.body, head, disc);
    this.laneX = this.laneXs[1];
    this.runner.position.set(this.laneX, this.groundY, 2);
    this.kit.root.add(this.runner);

    this.orbit.attach(this.kit.renderer.domElement);
    const rect = ctx.mount.getBoundingClientRect();
    this.resize(rect.width || 320, rect.height || 480);
  }

  private hop(): void {
    if (this.hopping || this.lives <= 0) return;
    this.hopping = true;
    this.hopV = this.hopV0;
    if (navigator.vibrate) navigator.vibrate(6);
  }

  private spawnObs(energy: number, onBeat: boolean): void {
    const lanes = [0, 1, 2].sort(() => Math.random() - 0.5);
    const count = onBeat && energy > 0.55 ? 2 : 1;
    for (let i = 0; i < count; i++) {
      const lane = lanes[i];
      const tall = Math.random() < (onBeat ? 0.55 : 0.4);
      const h = tall ? 1.75 : 0.52;
      const g = new THREE.Group();
      const block = new THREE.Mesh(
        new THREE.BoxGeometry(1.55, h, onBeat ? 1.3 : 1.0),
        new THREE.MeshStandardMaterial({
          color: tall ? 0xff5577 : 0xffaa44,
          emissive: tall ? 0x771122 : 0x664400,
          emissiveIntensity: onBeat ? 0.7 : 0.4,
          roughness: 0.4,
        }),
      );
      block.position.y = h / 2;
      g.add(block, makeGlowDisc(tall ? 0xff6688 : 0xffcc66, 0.95, onBeat ? 0.45 : 0.28));
      g.children[1].rotation.x = -Math.PI / 2;
      const z = -30 - Math.random() * 4;
      g.position.set(this.laneXs[lane], this.groundY, z);
      this.kit.root.add(g);
      this.obstacles.push({ mesh: g, z, lane, tall });
    }
  }

  private spawnPickup(): void {
    const free = [0, 1, 2];
    const lane = free[Math.floor(Math.random() * 3)];
    const roll = Math.random();
    const kind: Pickup['kind'] = roll < 0.7 ? 'note' : roll < 0.88 ? 'shield' : 'slow';
    const colors = { note: 0xffee66, shield: 0x66aaff, slow: 0xcc66ff };
    const g = new THREE.Group();
    const ball = new THREE.Mesh(
      new THREE.OctahedronGeometry(kind === 'note' ? 0.28 : 0.35, 0),
      new THREE.MeshStandardMaterial({
        color: colors[kind],
        emissive: colors[kind],
        emissiveIntensity: 0.75,
      }),
    );
    ball.position.y = 0.7;
    g.add(ball, makeGlowDisc(colors[kind], 0.55, 0.4));
    g.children[1].rotation.x = -Math.PI / 2;
    g.children[1].position.y = 0.05;
    const z = -28;
    g.position.set(this.laneXs[lane], this.groundY, z);
    this.kit.root.add(g);
    this.pickups.push({ mesh: g, z, lane, kind });
  }

  private hit(): void {
    if (this.shieldT > 0) {
      this.shieldT = 0;
      this.invuln = 0.9;
      this.message = 'SHIELD BROKE';
      this.messageT = 0.7;
      return;
    }
    this.lives -= 1;
    this.invuln = 1.3;
    if (navigator.vibrate) navigator.vibrate([14, 28, 14]);
    if (this.lives <= 0) {
      this.message = 'GAME OVER — FIRE TO RETRY';
      this.messageT = 99;
    } else {
      this.message = 'HIT';
      this.messageT = 0.65;
    }
  }

  private reset(): void {
    this.score = 0;
    this.lives = 3;
    this.wave = 1;
    this.lane = 1;
    this.laneX = this.laneXs[1];
    this.hopY = 0;
    this.hopV = 0;
    this.hopping = false;
    this.dist = 0;
    this.shieldT = 0;
    this.slowT = 0;
    this.spawnCd = 0.55;
    this.message = 'RUN THE BEAT';
    this.messageT = 1.3;
    for (const o of this.obstacles) {
      this.kit.root.remove(o.mesh);
      disposeObject(o.mesh);
    }
    for (const p of this.pickups) {
      this.kit.root.remove(p.mesh);
      disposeObject(p.mesh);
    }
    this.obstacles = [];
    this.pickups = [];
  }

  update(dt: number, input: InputState): void {
    if (this.disposed) return;
    const bands = this.ctx.getBands();
    const { energy, kick, beat, bass } = bands;
    const t = performance.now() * 0.001;

    if (this.messageT > 0) this.messageT -= dt;
    this.spawnCd = Math.max(0, this.spawnCd - dt);
    this.pickCd = Math.max(0, this.pickCd - dt);
    this.invuln = Math.max(0, this.invuln - dt);
    this.laneCd = Math.max(0, this.laneCd - dt);
    this.shieldT = Math.max(0, this.shieldT - dt);
    this.slowT = Math.max(0, this.slowT - dt);

    if (this.lives <= 0) {
      if (input.firePressed) this.reset();
      this.orbit.apply(this.kit.camera, this.camTarget, dt);
      this.kit.renderer.render(this.kit.scene, this.kit.camera);
      return;
    }

    if (input.left && !this.prevLeft && this.laneCd <= 0) {
      this.lane = Math.max(0, this.lane - 1);
      this.laneCd = 0.12;
    }
    if (input.right && !this.prevRight && this.laneCd <= 0) {
      this.lane = Math.min(2, this.lane + 1);
      this.laneCd = 0.12;
    }
    this.prevLeft = input.left;
    this.prevRight = input.right;

    const wantX = this.laneXs[this.lane];
    this.laneX += (wantX - this.laneX) * Math.min(1, dt * 14);

    if (input.firePressed || input.up) this.hop();
    if (this.hopping) {
      this.hopV -= this.gravity * dt;
      this.hopY += this.hopV * dt;
      if (this.hopY <= 0) {
        this.hopY = 0;
        this.hopV = 0;
        this.hopping = false;
      }
    }

    const slowMul = this.slowT > 0 ? 0.72 : 1;
    const scroll = (14 + energy * 11 + this.wave * 0.55 + kick * 3) * slowMul;
    this.dist += scroll * dt;
    this.score = Math.floor(this.dist * 3.5);
    this.wave = 1 + Math.floor(this.dist / 55);

    // beat rings pulse
    for (let i = 0; i < this.beatRings.length; i++) {
      const ring = this.beatRings[i];
      const mat = ring.material as THREE.MeshBasicMaterial;
      const pulseA = Math.max(0, beat * 0.9 + kick * 0.35 - i * 0.05);
      mat.opacity = pulseA * 0.7;
      ring.scale.setScalar(1 + pulseA * 1.4);
      ring.position.x = this.laneXs[this.lane] * 0.15;
    }

    // lane strip brightness
    this.track.children.forEach((ch, idx) => {
      if (idx % 2 === 1 && ch instanceof THREE.Mesh) {
        const m = ch.material as THREE.MeshBasicMaterial;
        m.color.setHex(0x33ffaa);
        m.opacity = 0.55 + kick * 0.4;
        m.transparent = true;
      }
    });

    this.runner.position.set(this.laneX, this.groundY + this.hopY, 2);
    this.runner.rotation.z = (wantX - this.laneX) * 0.15;
    this.body.scale.y = this.hopping ? 0.92 : 1 + Math.sin(this.dist * 10) * 0.04;
    this.runner.scale.setScalar(this.shieldT > 0 ? 1.08 + Math.sin(t * 10) * 0.03 : 1);
    this.runner.visible = this.invuln <= 0 || Math.floor(this.invuln * 14) % 2 === 0;

    const every = Math.max(0.48, 1.15 - energy * 0.4 - this.wave * 0.015);
    const onBeat = beat > 0.18;
    if (this.spawnCd <= 0 || (onBeat && this.spawnCd < every * 0.45)) {
      this.spawnObs(energy, onBeat);
      this.spawnCd = every * (onBeat ? 0.7 : 0.9) * (0.85 + Math.random() * 0.3);
    }
    if (this.pickCd <= 0) {
      this.spawnPickup();
      this.pickCd = 1.6 + Math.random() * 1.2 - energy * 0.3;
    }

    for (const o of this.obstacles) {
      o.z += scroll * dt;
      o.mesh.position.z = o.z;
      // pulse tall obstacles on bass
      o.mesh.scale.y = 1 + (o.tall ? bass * 0.08 : 0);
    }
    for (const p of this.pickups) {
      p.z += scroll * dt;
      p.mesh.position.z = p.z;
      p.mesh.rotation.y += dt * 3;
      p.mesh.position.y = this.groundY + Math.sin(t * 4 + p.z) * 0.12;
    }

    for (const o of this.obstacles) {
      if (this.invuln > 0) continue;
      if (o.z < 0.6 || o.z > 3.4) continue;
      if (o.lane !== this.lane) continue;
      if (o.tall) {
        this.hit();
        o.mesh.visible = false;
      } else if (this.hopY < 0.45) {
        this.hit();
        o.mesh.visible = false;
      }
    }

    for (const p of this.pickups) {
      if (p.z < 0.5 || p.z > 3.5) continue;
      if (p.lane !== this.lane) continue;
      if (this.hopY > 0.7) continue;
      if (p.kind === 'note') {
        this.score += 15 + Math.floor(energy * 20);
      } else if (p.kind === 'shield') {
        this.shieldT = 9;
        this.message = 'SHIELD';
        this.messageT = 0.9;
      } else {
        this.slowT = 5;
        this.message = 'SLOW-MO';
        this.messageT = 0.9;
      }
      p.mesh.visible = false;
      if (navigator.vibrate) navigator.vibrate(8);
    }

    this.obstacles = this.obstacles.filter((o) => {
      if (!o.mesh.visible || o.z > 8) {
        this.kit.root.remove(o.mesh);
        disposeObject(o.mesh);
        return false;
      }
      return true;
    });
    this.pickups = this.pickups.filter((p) => {
      if (!p.mesh.visible || p.z > 8) {
        this.kit.root.remove(p.mesh);
        disposeObject(p.mesh);
        return false;
      }
      return true;
    });

    this.track.position.z = (this.dist * 0.55) % 4;
    this.camTarget.set(this.laneX * 0.35, 0.6 + this.hopY * 0.15, -1.5);
    this.orbit.apply(this.kit.camera, this.camTarget, dt);
    this.kit.renderer.render(this.kit.scene, this.kit.camera);
  }

  resize(w: number, h: number): void {
    if (!this.kit) return;
    resizeThree(this.kit, w, h, 16, 20);
  }

  getHud() {
    const buff = [this.shieldT > 0 ? '🛡' : '', this.slowT > 0 ? '⏱' : ''].join('');
    return {
      score: this.score,
      lives: Math.max(0, this.lives),
      wave: this.wave,
      message: this.messageT > 0 ? this.message : buff || undefined,
    };
  }

  dispose(): void {
    this.disposed = true;
    this.orbit.detach();
    for (const o of this.obstacles) disposeObject(o.mesh);
    for (const p of this.pickups) disposeObject(p.mesh);
    this.kit?.dispose();
  }
}
